import { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlay, FaPause, FaRedo } from 'react-icons/fa';
import styles from './PomodoroTimer.module.css';

const WORK_MINUTES = 25;
const BREAK_MINUTES = 5;

function playBeep() {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 800;
  osc.type = 'sine';
  gain.gain.value = 0.3;
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
  osc.stop(ctx.currentTime + 0.8);
}

function PomodoroTimer() {
  const [isBreak, setIsBreak] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WORK_MINUTES * 60);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef(null);

  const totalSeconds = isBreak ? BREAK_MINUTES * 60 : WORK_MINUTES * 60;
  const progress = ((totalSeconds - timeLeft) / totalSeconds) * 100;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  const reset = useCallback(() => {
    setIsRunning(false);
    setIsBreak(false);
    setTimeLeft(WORK_MINUTES * 60);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            playBeep();
            setIsBreak((b) => {
              const next = !b;
              setTimeLeft(next ? BREAK_MINUTES * 60 : WORK_MINUTES * 60);
              return next;
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning]);

  const circumference = 2 * Math.PI * 120;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={`${styles.timer} ${isBreak ? styles.breakMode : styles.workMode}`}>
      <div className={styles.modeLabel}>{isBreak ? 'Break' : 'Work'}</div>

      <div className={styles.circleWrapper}>
        <svg className={styles.svg} viewBox="0 0 260 260">
          <circle
            className={styles.trackCircle}
            cx="130" cy="130" r="120"
            fill="none"
            strokeWidth="6"
          />
          <circle
            className={styles.progressCircle}
            cx="130" cy="130" r="120"
            fill="none"
            strokeWidth="6"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform="rotate(-90 130 130)"
          />
        </svg>
        <div className={styles.timeDisplay}>
          {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
        </div>
      </div>

      <div className={styles.controls}>
        <button
          className={styles.controlBtn}
          onClick={() => setIsRunning(!isRunning)}
          aria-label={isRunning ? 'Pause' : 'Start'}
        >
          {isRunning ? <FaPause /> : <FaPlay />}
        </button>
        <button className={styles.controlBtn} onClick={reset} aria-label="Reset">
          <FaRedo />
        </button>
      </div>
    </div>
  );
}

export default PomodoroTimer;
